/**
 * De onde a tela Estoque Full tira os dados.
 *
 * Desde 25/09/2026 (pedido da Mariana: "tudo tem que carregar em tempo real"):
 *   1. tenta o serviço `/api/estoque-full/dados` (services/estoque-full), que consulta o Mongo
 *      NA HORA e casa com o retrato mais recente do estoque;
 *   2. se o serviço não responder, cai no arquivo estático `public/data/estoque-full.json`,
 *      publicado junto com o site — e a tela DIZ que é arquivo e de quando.
 *
 * ANTES ERA SÓ O PASSO 2, e esse era o defeito: o arquivo só mudava quando alguém rodava os
 * quatro scripts na mão e republicava o BI. O painel chegou a ficar três dias parado.
 *
 * O QUE "TEMPO REAL" SIGNIFICA AQUI. As duas metades do painel têm relógios diferentes:
 *
 *   vendas, receita, margem, CMV   Mongo, consultado a cada carga da tela — segundos
 *   estoque no CD, visitas          API do ML, ~600 chamadas, 2 a 12 min — retrato do cron
 *
 * Por isso `FonteInfo` carrega DUAS horas, e a tela mostra as duas. Dizer "estoque ao vivo"
 * seria mentira: entre uma coleta e outra, o número de unidades no CD é o da última leitura.
 */
import type { EstoqueFullData } from './types'

export type Fonte = 'arquivo' | 'servico'

export type DadosEstoqueFull = {
  dados: EstoqueFullData
  fonte: Fonte
  /** Dias entre a coleta do estoque e agora. Alimenta o aviso de dado velho. */
  idadeDias: number
  avisos: string[]
  /** Hora da consulta ao Mongo (só quando veio do serviço). */
  consultaMongo?: string | null
  /** Hora em que o estoque foi lido na API do ML. */
  estoqueEm?: string | null
  /** Há quantos minutos o estoque foi lido (só quando veio do serviço). */
  estoqueHaMin?: number | null
  /** Uma coleta completa está rodando agora. */
  coletando?: boolean
  /** Por que caiu no arquivo, quando caiu. */
  motivoArquivo?: string | null
}

export type StatusMotor = {
  ok: boolean
  coletando: boolean
  tem_retrato: boolean
  estoque_coletado_ha_min: number | null
  payload_ha_min: number | null
  ultima_coleta: { em_sp?: string; duracao_segundos?: number; ok?: boolean; erro?: string | null } | null
  cache_min: number
}

export const ARQUIVO = 'data/estoque-full.json'
const API = '/api/estoque-full'

/** A partir de quantos dias a tela passa a avisar que o retrato do estoque envelheceu. O
 *  estoque do Full muda todo dia; com o cron de pé isso deixa de aparecer. */
export const IDADE_ALERTA_DIAS = 7

/** De quanto em quanto tempo a tela pergunta ao motor se a coleta terminou. */
export const POLL_MS = 15_000

function idadeEmDias(iso: string): number {
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return 0
  return Math.max(0, Math.floor((Date.now() - t) / 86_400_000))
}

function conferir(d: EstoqueFullData): string[] {
  const avisos: string[] = []
  if (d.meta.visitas_erros > 0) {
    avisos.push(
      `${d.meta.visitas_erros} anúncio(s) não devolveram visitas na coleta — a conversão deles fica em branco.`,
    )
  }
  if (d.demanda_nao_atribuida.length > 0) {
    const un = d.demanda_nao_atribuida.reduce((a, x) => a + x.un30, 0)
    avisos.push(
      `${un} unidade(s) vendidas em anúncio com variação não puderam ser atribuídas a um SKU — a demanda desses SKUs está subestimada.`,
    )
  }
  const vf = d.meta.vendas_fonte
  if (vf?.fonte === 'mongo') {
    avisos.push(
      vf.aviso ??
        '"Vendeu 30d" está vindo do Mongo (todas as logísticas), não só do que saiu do CD — a coleta da API ainda não rodou.',
    )
  } else if (vf && vf.erros_envios > 0) {
    avisos.push(
      `${vf.erros_envios} envio(s) não responderam na API do ML — ${vf.un_erro_envio} unidade(s) ficaram sem classificar, fora tanto de "Vendeu 30d" quanto de "fora do CD". A próxima coleta tenta de novo.`,
    )
  }
  if (d.meta.fora_cd_sem_sku && d.meta.fora_cd_sem_sku.length > 0) {
    const un = d.meta.fora_cd_sem_sku.reduce((a, x) => a + x.un30, 0)
    avisos.push(
      `${un} unidade(s) vendidas por Flex/coleta em anúncio com variações não puderam ser atribuídas a uma cor — não aparecem como "fora do CD" em nenhum SKU.`,
    )
  }
  const aviso = (d.meta as { aviso_servico?: string }).aviso_servico
  if (aviso) avisos.push(aviso)
  return avisos
}

type FonteServico = {
  consulta_mongo?: string
  estoque_coletado_em?: string | null
  estoque_coletado_ha_min?: number | null
  coletando?: boolean
}

async function doServico(signal?: AbortSignal, fresco = false): Promise<DadosEstoqueFull> {
  const resp = await fetch(`${API}/dados${fresco ? '?fresco=1' : ''}`, {
    signal,
    credentials: 'include',
    cache: 'no-store',
  })
  if (!resp.ok) {
    const corpo = (await resp.json().catch(() => null)) as { detail?: string } | null
    throw new Error(corpo?.detail || `HTTP ${resp.status}`)
  }
  const dados = (await resp.json()) as EstoqueFullData
  if (!dados?.skus?.length) throw new Error('serviço devolveu payload sem SKUs')
  const f = ((dados.meta as { fonte_servico?: FonteServico }).fonte_servico || {}) as FonteServico
  return {
    dados,
    fonte: 'servico',
    idadeDias: idadeEmDias(dados.gerado_em_iso),
    avisos: conferir(dados),
    consultaMongo: f.consulta_mongo ?? null,
    estoqueEm: f.estoque_coletado_em ?? null,
    estoqueHaMin: f.estoque_coletado_ha_min ?? null,
    coletando: Boolean(f.coletando),
  }
}

async function doArquivo(motivo: string, signal?: AbortSignal): Promise<DadosEstoqueFull> {
  const base = import.meta.env.BASE_URL || '/'
  const resp = await fetch(`${base}${ARQUIVO}`, { signal, cache: 'no-store' })
  if (!resp.ok) {
    throw new Error(
      `O serviço não respondeu (${motivo}) e também não consegui ler ${ARQUIVO} (HTTP ${resp.status}).`,
    )
  }
  const dados = (await resp.json()) as EstoqueFullData
  if (!dados?.skus || !Array.isArray(dados.skus)) {
    throw new Error(`${ARQUIVO} veio sem a lista de SKUs — a coleta provavelmente falhou no meio.`)
  }
  return {
    dados,
    fonte: 'arquivo',
    idadeDias: idadeEmDias(dados.gerado_em_iso),
    avisos: conferir(dados),
    estoqueEm: dados.gerado_em,
    motivoArquivo: motivo,
  }
}

/** Serviço primeiro; arquivo como reserva. `fresco` ignora o cache curto do serviço. */
export async function carregarEstoqueFull(
  signal?: AbortSignal,
  fresco = false,
): Promise<DadosEstoqueFull> {
  try {
    return await doServico(signal, fresco)
  } catch (e) {
    if ((e as Error)?.name === 'AbortError') throw e
    return doArquivo(e instanceof Error ? e.message : 'motivo desconhecido', signal)
  }
}

/** Estado do motor, para o botão e o aviso. Devolve null quando não há serviço no ar. */
export async function statusMotor(signal?: AbortSignal): Promise<StatusMotor | null> {
  try {
    const r = await fetch(`${API}/status`, { signal, credentials: 'include', cache: 'no-store' })
    if (!r.ok) return null
    return (await r.json()) as StatusMotor
  } catch {
    return null
  }
}

/**
 * Pede uma coleta completa do estoque na API do ML. Volta na hora — a coleta roda no servidor
 * por alguns minutos, e quem acompanha é `statusMotor`.
 *
 * O token de escrita é injetado pelo nginx do BI; o browser nunca o vê.
 */
export async function pedirColeta(): Promise<{ ok: boolean; mensagem: string }> {
  const r = await fetch(`${API}/atualizar`, { method: 'POST', credentials: 'include' })
  const corpo = (await r.json().catch(() => null)) as
    | { ok?: boolean; mensagem?: string; detail?: string }
    | null
  if (!r.ok) {
    return { ok: false, mensagem: corpo?.detail || `não consegui pedir a coleta (HTTP ${r.status})` }
  }
  return { ok: true, mensagem: corpo?.mensagem || 'coleta iniciada' }
}
